import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { formatMinor, parseAmountToMinor } from "@convex/payments/domain";
import { Storefront } from "@phosphor-icons/react";
import { getStoredUserSession } from "@shared/hooks/useUserSession";
import { brandify } from "../components/brand";

/**
 * QR-ul static al unei locații (PRD §4.3). Consumatorul introduce suma doar
 * dacă flagul `consumerAmountEntryEnabled` e pornit; altfel îi spunem să
 * ceară comerciantului un QR dinamic.
 */

export const Route = createFileRoute("/m/$slug")({
  head: () => ({ meta: [{ title: "Plătește — suuper" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: StaticQrPage,
});

function StaticQrPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const location = useQuery(api.merchants.getLocationBySlug, { slug });
  const createIntent = useMutation(api.payments.intents.createConsumerPaymentIntent);

  const [amountText, setAmountText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // O cheie per formular: dublu-click sau reîncercarea după eroare de rețea
  // nu pot crea două intenții.
  const idempotencyKey = useRef(crypto.randomUUID());

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!location || submitting) return;
    const amountMinor = parseAmountToMinor(amountText);
    if (amountMinor === null) return setError("Introdu o sumă validă, de exemplu 37,50.");
    if (amountMinor < location.minAmountMinor) return setError(`Suma minimă este ${formatMinor(location.minAmountMinor)}.`);
    if (amountMinor > location.maxAmountMinor) return setError(`Suma maximă este ${formatMinor(location.maxAmountMinor)}.`);

    setSubmitting(true);
    setError(null);
    try {
      const { userId, sessionToken } = getStoredUserSession();
      const consumer = userId && sessionToken ? { userId, sessionToken } : undefined;
      const result = await createIntent({ locationId: location.locationId, amountMinor, idempotencyKey: idempotencyKey.current, consumer });
      if (result.ok) {
        navigate({ to: "/pay/$intentId", params: { intentId: result.id } });
        return;
      }
      setError(result.error === "consumerAmountEntryDisabled" ? "Cere comerciantului să-ți arate un cod QR de plată." : "Plata nu a putut fi pregătită. Încearcă din nou.");
    } catch (err) {
      console.error("[m] creare intenție eșuată", err);
      setError("Plata nu a putut fi pregătită. Încearcă din nou.");
    }
    setSubmitting(false);
  };

  return (
    <main className="min-h-screen bg-background px-4 py-8">
      <div className="max-w-md mx-auto">
        <p className="text-center text-xl font-bold mb-6">{brandify("suuper")}</p>

        {location === undefined && (
          <div className="w-10 h-10 mx-auto animate-spin motion-reduce:animate-none rounded-full border-4 border-primary border-t-transparent" />
        )}

        {location === null && <p className="text-center text-lg font-semibold text-text">Locație inexistentă.</p>}

        {location && (
          <>
            <header className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-3 bg-primary/10 rounded-2xl flex items-center justify-center">
                <Storefront size={28} className="text-primary" />
              </div>
              <p className="text-2xl font-bold text-text" data-testid="merchant-name">
                {location.merchantName}
              </p>
              <p className="text-sm text-text-muted">
                {location.name} · {location.city}
              </p>
            </header>

            {location.consumerAmountEntryEnabled ? (
              <form onSubmit={submit} className="bg-white rounded-2xl p-5 shadow-sm">
                <label htmlFor="amount" className="block text-sm font-medium text-text mb-2">
                  Cât plătești?
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="amount"
                    data-testid="amount-input"
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder="37,50"
                    value={amountText}
                    onChange={(e) => setAmountText(e.target.value)}
                    className="flex-1 text-3xl font-bold px-4 py-3 rounded-xl border-2 border-border focus:border-primary outline-none"
                  />
                  <span className="text-xl font-semibold text-text-muted">lei</span>
                </div>
                {error && (
                  <p role="alert" className="mt-3 text-sm text-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={submitting || amountText.trim() === ""}
                  data-testid="create-payment-submit"
                  className="mt-5 w-full py-4 bg-primary text-white font-semibold rounded-xl disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Se pregătește…" : "Continuă"}
                </button>
              </form>
            ) : (
              <div className="bg-white rounded-2xl p-5 shadow-sm text-center">
                <p className="text-text">Cere comerciantului să-ți arate un cod QR de plată.</p>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
